import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import { Step } from "@redotech/redo-model/return-flow";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const SPECIAL_LOCATION_ID = {
  ORIGINAL: "original",
  SHIPMENT_ORIGIN: "shipment-origin",
  REDO_MANAGED: "redo-managed",
} as const;

export type SpecialLocationId =
  (typeof SPECIAL_LOCATION_ID)[keyof typeof SPECIAL_LOCATION_ID];

export interface SetReturnLocationsState {
  locations: string[];
  restockExternalLocationIds?: Record<string, string | null>;
  customTitle?: string;
}

export namespace SetReturnLocationsState {
  export function valid(state: SetReturnLocationsState): boolean {
    const validLocations = state.locations.filter((loc) => loc !== "");
    if (validLocations.length === 0) {
      return false;
    }

    // Check that all regular locations have valid restock configuration
    for (const locationId of validLocations) {
      if (
        locationId === SPECIAL_LOCATION_ID.ORIGINAL ||
        locationId === SPECIAL_LOCATION_ID.REDO_MANAGED
      ) {
        continue;
      }

      const restockId = state.restockExternalLocationIds?.[locationId];
      if (!restockId) {
        return false;
      }
    }

    return true;
  }
}

export const SET_RETURN_LOCATIONS = (
  Details: React.ComponentType<StepTypeDetailsProps<SetReturnLocationsState>>,
): StepType<SetReturnLocationsState, Step.SetReturnLocations> => ({
  layout() {
    return BlockLayout.FULL;
  },
  empty: { locations: [] },

  title: "Location",

  customTitle: (state) => state.customTitle,

  description: (state) => {
    const validCount = state.locations.filter((loc) => loc !== "").length;
    return `${validCount} location${validCount !== 1 ? "s" : ""}`;
  },

  Icon: MarkerPinIcon,
  iconColor: "success",

  Details(props) {
    return <Details {...props} />;
  },

  downstream: () => [],

  maxDownstream: () => 0,

  fromModel: (model) => {
    const locations = [...model.locations];
    if (model.useOriginalLocation) {
      locations.push(SPECIAL_LOCATION_ID.ORIGINAL);
    }
    if (model.useOriginalFulfillmentAddress) {
      locations.push(SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN);
    }
    if (model.useRedoManaged) {
      locations.push(SPECIAL_LOCATION_ID.REDO_MANAGED);
    }
    return {
      locations,
      customTitle: model.customTitle,
      restockExternalLocationIds: model.restockExternalLocationIds,
    };
  },

  toModel: (state) => {
    const regularLocations: string[] = [];
    let useOriginalLocation = false;
    let useOriginalFulfillmentAddress = false;
    let useRedoManaged = false;

    for (const locationId of state.locations) {
      if (!locationId) {
        continue;
      }

      switch (locationId) {
        case SPECIAL_LOCATION_ID.ORIGINAL: {
          useOriginalLocation = true;

          break;
        }
        case SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN: {
          useOriginalFulfillmentAddress = true;

          break;
        }
        case SPECIAL_LOCATION_ID.REDO_MANAGED: {
          useRedoManaged = true;

          break;
        }
        default: {
          regularLocations.push(locationId);
        }
      }
    }

    return {
      type: Step.SET_RETURN_LOCATIONS,
      locations: regularLocations,
      customTitle: state.customTitle,
      restockExternalLocationIds: state.restockExternalLocationIds,
      useOriginalLocation: useOriginalLocation || undefined,
      useOriginalFulfillmentAddress: useOriginalFulfillmentAddress || undefined,
      useRedoManaged: useRedoManaged || undefined,
    };
  },

  valid: (state) => SetReturnLocationsState.valid(state),

  stepDeleted: (state) => state,
});
